import React, { useState, useRef, useEffect } from "react";
import { commandMap } from "../commands";
import { loadFilesystem } from "../filesystem";
import { Directory } from "../filesystem";
import { colorize } from "../utils/colorize";
import { getPrompt } from "../utils/promptUtils";

const Terminal: React.FC = () => {
    const [history, setHistory] = useState<string[]>([]);
    const [input, setInput] = useState("");
    const [commandHistory, setCommandHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filesystemReady, setFilesystemReady] = useState(false);
    const [currentDir, setCurrentDir] = useState<Directory>(null);
    const [rootDir, setRootDir] = useState<Directory | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadFilesystem().then(async dir => {
            setCurrentDir(dir);
            setRootDir(dir);
            setFilesystemReady(true);

            const version = process.env.REACT_APP_VERSION ?? "1.0.0";

            setHistory([
                `Welcome to Terminal-Resume v${version}`,
                "by Cezary924 (Cezary Zięba)\n\n",
                `Last login: ${new Date().toString()} from 127.0.0.1\n\n`,
                "Type `help` to see available commands.\n",
            ]);
        });
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }, [history]);

    const handleCommand = async (inputLine: string) => {
        const [cmdName, ...args] = inputLine.trim().split(/\s+/);
        const cmd = commandMap[cmdName];
        const prompt = getPrompt(currentDir);

        if (!inputLine.trim()) {
            setHistory(prev => [...prev, `${prompt}`]);
            return;
        }

        let newLines = [`${prompt} ${inputLine}`];

        const result = cmd
            ? await cmd.run(args, { currentDir, setCurrentDir, rootDir })
            : `ERROR: Command not found: ${cmdName}`;

        if (typeof result === "string" && result.trim()) {
            newLines.push(result);
        }

        setHistory(prev => [...prev, ...newLines]);
        setCommandHistory(prev => [...prev, inputLine]);
        setHistoryIndex(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleCommand(input);
            setInput("");
        } else if (e.key === "ArrowUp") {
            if (commandHistory.length === 0) return;
            const newIndex = historyIndex === null ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
            setInput(commandHistory[newIndex]);
            setHistoryIndex(newIndex);
            setTimeout(() => {
                const el = inputRef.current;
                if (el) {
                    el.selectionStart = el.selectionEnd = el.value.length;
                }
            }, 0);
        } else if (e.key === "ArrowDown") {
            if (commandHistory.length === 0 || historyIndex === null) return;
            const newIndex = Math.min(commandHistory.length - 1, historyIndex + 1);
            setInput(commandHistory[newIndex]);
            setHistoryIndex(newIndex);
            setTimeout(() => {
                const el = inputRef.current;
                if (el) {
                    el.selectionStart = el.selectionEnd = el.value.length;
                }
            }, 0);
        } else if (e.key === "Tab") {
            e.preventDefault();
            const words = input.trim().split(/\s+/);
            const [first, ...rest] = words;
            if (words.length === 1) {
                const matches = Object.keys(commandMap).filter(cmd => cmd.startsWith(first));
                if (matches.length === 1) {
                    setInput(matches[0] + " ");
                } else if (matches.length > 1) {
                    const prompt = getPrompt(currentDir);
                    setHistory(prev => [...prev, `${prompt} ${input}`, matches.join("  ")]);
                }
            } else {
                const lastWord = rest[rest.length - 1];
                const matches = currentDir.children
                    .filter(f => f.name.startsWith(lastWord))
                    .map(f => f.name);
                if (matches.length === 1) {
                    const newArgs = [...rest.slice(0, -1), matches[0]];
                    setInput(first + " " + newArgs.join(" "));
                } else if (matches.length > 1) {
                    const prompt = getPrompt(currentDir);
                    setHistory(prev => [...prev, `${prompt} ${input}`, matches.join("  ")]);
                }
            }
        }
    };

    useEffect(() => {
        inputRef.current?.focus();
    }, [history]);

    const prompt = getPrompt(currentDir);

    if (!filesystemReady) return <div>System is being loaded...</div>;

    return (
        <div
            className="terminal-container"
            onClick={() => inputRef.current?.focus()}
        >
            <div className="terminal-output">
                {history.map((line, idx) => (
                    <div
                        key={idx}
                        className="terminal-line"
                        dangerouslySetInnerHTML={{ __html: colorize(line) }}
                    />
                ))}
                <div ref={bottomRef} />
            </div>
            <div className="terminal-input-line">
                <span className="terminal-prompt">{prompt}</span>
                <input
                    ref={inputRef}
                    className="terminal-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>
        </div>
    );
};

export default Terminal;